// Basic
import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
// Components
import Entity from '../../../entity/Entity';
import Select from '@/common-utils/components/ui/select';
import PopoverHelper from '../../../popover/PopoverHelper';
import { Button } from '@/common-utils/components/ui/button';
import { Input } from '@/common-utils/components/ui/input';
// Common
import ICONS from '@/common-utils/enums/icons';
import NUMBER_OPTIONS from './NumberOptions';
import buildDictionary from '@/common-utils/functions/buildDictionary';
import Icon from '../../../icon/Icon';
import getTranslatedText from '@/common-utils/functions/getTranslatedText';

/**
 * @param root0
 * @param root0.addFilter
 * @param root0.columnId
 * @param root0.filterByText
 * @param root0.resetThisFilter
 * @param root0.setLaunchSearch
 * @returns NumberFilter
 */
function NumberFilter({ addFilter, columnId, filterByText, resetThisFilter, setLaunchSearch }) {

    // Constants
    const options = buildDictionary(NUMBER_OPTIONS, 'entity', 'name');

    // State
    const [isRange, setIsRange] = useState(false);
    const [selectedValue, setSelectedValue] = useState(undefined);
    const [isNumberFilterOpen, setIsNumberFilterOpen] = useState(true);
    const [inputValue, setInputValue] = useState('');
    const [endInputValue, setEndInputValue] = useState('');
    const [isDirty, setIsDirty] = useState(false);

    // Hooks
    useEffect(() => {
        if (resetThisFilter && isDirty) {
            setInputValue('');
            setEndInputValue('');
            setSelectedValue(undefined);
            setIsDirty(false);
        }
    }, [resetThisFilter]);

    // Functions
    const onChange = (selectedVal) => {
        setSelectedValue(selectedVal);
        if (selectedVal === NUMBER_OPTIONS.RANGE.name) {
            setIsRange(true);
        } else {
            setIsRange(false);
        }
        setIsDirty(true);
    };

    const updateFilter = () => {
        let filterValue;
        switch (selectedValue) {
            case NUMBER_OPTIONS.EQUAL.name: {
                filterValue = inputValue;
                break;
            }
            case NUMBER_OPTIONS.MORE.name: {
                filterValue = `${inputValue}__value`;
                break;
            }
            case NUMBER_OPTIONS.LESS.name: {
                filterValue = `value__${inputValue}`;
                break;
            }
            case NUMBER_OPTIONS.RANGE.name: {
                filterValue = `${inputValue}__value__${endInputValue}`;
                break;
            }
        }
        addFilter(
            columnId,
            filterValue
        );
        setIsDirty(true);
        setIsNumberFilterOpen(false);
        setLaunchSearch(true);
    };

    const onOpenChange = () => {
        setIsNumberFilterOpen(!isNumberFilterOpen);
    };

    return (
        <>
            <PopoverHelper
                isOpen={isNumberFilterOpen}
                onOpenChange={onOpenChange}
                asChild
                trigger={
                    <Button
                        variant={'outline'}
                        className='pl-3 text-left font-normal min-w-60 h-10 rounded-sm'
                    >
                        {selectedValue && isDirty ? (
                            !isRange ?
                                <>
                                    <Entity nowrap entity={`number_filter.${selectedValue}`} className={'mr-1'} />
                                    {inputValue}
                                </>
                                :
                                <>
                                    <Entity nowrap entity={`number_filter.${selectedValue}.between`} className={'mr-1'} />
                                    {inputValue}
                                    <Entity nowrap entity={`number_filter.${selectedValue}.and`} className={'mx-1'} />
                                    {endInputValue}
                                </>
                        ) : (
                            <Icon icon={ICONS.AMOUNT}></Icon>
                        )}
                    </Button>
                }
                content={
                    <div className='flex flex-col'>
                        <div className='flex items-center'>
                            <Select
                                placeholder={getTranslatedText(`number_filter.filter_by.${filterByText}`)}
                                options={options}
                                onChange={onChange}
                                className={'min-w-48 items-center'}
                                noValidateIcon={true}
                            />
                        </div>
                        {
                            selectedValue &&
                            <>
                                <div className='flex items-center mt-2'>
                                    {
                                        !isRange &&
                                        <>
                                            <Entity nowrap entity={`number_filter.${selectedValue}`} className={'mr-1'} />
                                            <Input type='number' value={inputValue} onChange={(event) => {
                                                setInputValue(event?.target?.value);
                                            }} />
                                        </>
                                    }
                                    {
                                        isRange &&
                                        <>
                                            <Entity nowrap entity={`number_filter.${selectedValue}.between`} className={'mr-1'} />
                                            <Input type='number' value={inputValue} onChange={(event) => {
                                                setInputValue(event?.target?.value);
                                            }} />
                                            <Entity nowrap entity={`number_filter.${selectedValue}.and`} className={'mx-1'} />
                                            <Input type='number' value={endInputValue} onChange={(event) => {
                                                setEndInputValue(event?.target?.value);
                                            }} />
                                        </>
                                    }
                                </div>
                                <Button entity='button.submit'
                                    onClick={updateFilter}
                                    variant='primary'
                                    className={'mt-2'}
                                    disabled={isRange ? !inputValue || !endInputValue : !inputValue}
                                />
                            </>
                        }
                    </div>
                }
                contentClass={'p-2'}
            />
        </>
    );
}

NumberFilter.propTypes = {
    addFilter: PropTypes.func,
    columnId: PropTypes.string,
    filterByText: PropTypes.string,
    resetThisFilter: PropTypes.bool,
    setLaunchSearch: PropTypes.func
};

export default NumberFilter;